export type EncodedAudio = {
  mp3Blob: Blob;
  durationMs: number;
  sizeBytes: number;
  sampleRate: number;
};

export type AudioValidationOptions = {
  minDurationSeconds: number;
  maxDurationSeconds: number;
  maxSizeMB: number;
};

export type AudioValidationResult =
  | { ok: true }
  | { ok: false; reason: string };

const DEFAULT_BITRATE = 128; // Optimized for voice: good quality, smaller file size for faster uploads
const MAX_SAMPLES_PER_FRAME = 1152;
const CANONICAL_SAMPLE_RATE = 48000; // Phase 3: Standardize to 48k
const ENABLE_RESAMPLING = true; // Phase 3: Enabled for MP4 pipeline consistency

export async function encodeToMp3(
  sourceBlob: Blob,
  bitrateKbps = DEFAULT_BITRATE,
): Promise<EncodedAudio> {
  if (typeof window === "undefined") {
    throw new Error("encodeToMp3 must run in the browser");
  }

  // Validate blob
  if (sourceBlob.size < 100) {
    throw new Error(`Audio blob too small: ${sourceBlob.size} bytes`);
  }

  const AudioContextCtor =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;

  if (!AudioContextCtor) {
    throw new Error("Web Audio API is not supported in this browser");
  }

  const arrayBuffer = await sourceBlob.arrayBuffer();
  const audioContext = new AudioContextCtor();

  try {
    let audioBuffer: AudioBuffer;
    try {
      audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    } catch (decodeError) {
      console.error("Failed to decode audio data:", {
        blobSize: sourceBlob.size,
        blobType: sourceBlob.type,
        arrayBufferSize: arrayBuffer.byteLength,
        error: decodeError,
      });
      throw new Error(
        `Unable to decode audio data. Blob type: ${sourceBlob.type || "unknown"}, size: ${sourceBlob.size} bytes. ` +
        `This may indicate the recording format is not supported.`
      );
    }

    // Fast-path detection: skip processing if audio is already optimal
    if (needsAudioProcessing(audioBuffer)) {
      // Process audio with fallback to legacy pipeline if single-pass fails
      audioBuffer = await encodeWithFallback(audioBuffer, {
        targetChannels: 1,
        targetSampleRate: CANONICAL_SAMPLE_RATE,
        amplitudeLimit: 0.95,
        amplitudeBoostThreshold: 0.7,
        amplitudeBoostTarget: 0.85,
      });
    }
    // If fast-path applies (needsAudioProcessing returns false), skip processing and proceed directly to MP3 encoding

    const mp3Blob = await convertToMp3Blob(audioBuffer, bitrateKbps);

    return {
      mp3Blob,
      durationMs: audioBuffer.duration * 1000,
      sizeBytes: mp3Blob.size,
      sampleRate: audioBuffer.sampleRate,
    };
  } finally {
    audioContext.close();
  }
}

function floatTo16BitPCM(input: Float32Array): Int16Array {
  const buffer = new Int16Array(input.length);

  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }

  return buffer;
}

/**
 * Phase 3.1: Enforce mono defensively - downmix to mono if >1 channels
 */
async function downmixToMono(audioBuffer: AudioBuffer): Promise<AudioBuffer> {
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer;
  }

  // Downmix: average all channels
  const length = audioBuffer.length;
  const monoData = new Float32Array(length);
  const numChannels = audioBuffer.numberOfChannels;

  for (let i = 0; i < length; i++) {
    let sum = 0;
    for (let channel = 0; channel < numChannels; channel++) {
      sum += audioBuffer.getChannelData(channel)[i];
    }
    monoData[i] = sum / numChannels;
  }

  // Create new mono buffer using OfflineAudioContext
  const OfflineAudioContextCtor =
    window.OfflineAudioContext ||
    (window as typeof window & { webkitOfflineAudioContext?: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  if (!OfflineAudioContextCtor) {
    throw new Error("OfflineAudioContext not available for downmix");
  }

  const offlineContext = new OfflineAudioContextCtor(
    1, // channels (mono)
    length,
    audioBuffer.sampleRate
  );

  // Create buffer with mono data and render it to get a proper AudioBuffer
  const tempBuffer = offlineContext.createBuffer(1, length, audioBuffer.sampleRate);
  tempBuffer.copyToChannel(monoData, 0);
  
  // Render through OfflineAudioContext to get a new AudioBuffer
  const source = offlineContext.createBufferSource();
  source.buffer = tempBuffer;
  source.connect(offlineContext.destination);
  source.start();

  return await offlineContext.startRendering();
}

/**
 * Phase 3.2: Resample to canonical 48k sample rate if needed
 */
async function resampleToCanonicalRate(
  audioBuffer: AudioBuffer
): Promise<AudioBuffer> {
  if (audioBuffer.sampleRate === CANONICAL_SAMPLE_RATE) {
    return audioBuffer;
  }

  const OfflineAudioContextCtor =
    window.OfflineAudioContext ||
    (window as typeof window & { webkitOfflineAudioContext?: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  if (!OfflineAudioContextCtor) {
    throw new Error("OfflineAudioContext not available for resampling");
  }

  const length = Math.ceil(audioBuffer.duration * CANONICAL_SAMPLE_RATE);
  const offlineContext = new OfflineAudioContextCtor(
    1, // channels (mono)
    length,
    CANONICAL_SAMPLE_RATE
  );

  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineContext.destination);
  source.start();

  return await offlineContext.startRendering();
}

/**
 * Calculate max amplitude from audio buffer
 */
function getMaxAmplitude(audioBuffer: AudioBuffer): number {
  let maxAmplitude = 0;
  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const channelData = audioBuffer.getChannelData(channel);
    for (let i = 0; i < channelData.length; i++) {
      const abs = Math.abs(channelData[i]);
      if (abs > maxAmplitude) {
        maxAmplitude = abs;
      }
    }
  }
  return maxAmplitude;
}

/**
 * Check if audio buffer needs processing (downmixing, resampling, or amplitude adjustment).
 * Returns false if audio is already optimal (fast path), true if processing is needed.
 * This function only reads AudioBuffer properties and does not create any AudioContext instances.
 */
function needsAudioProcessing(audioBuffer: AudioBuffer): boolean {
  // Check channel count: return true (needs processing) if not mono
  if (audioBuffer.numberOfChannels !== 1) {
    return true;
  }

  // Check sample rate: return true (needs processing) if not canonical rate
  if (audioBuffer.sampleRate !== CANONICAL_SAMPLE_RATE) {
    return true;
  }

  // Calculate max amplitude using existing getMaxAmplitude() function
  const maxAmplitude = getMaxAmplitude(audioBuffer);

  // Check amplitude range: return true (needs processing) if needs limiting or boosting
  if (maxAmplitude > 0.95 || maxAmplitude < 0.7) {
    return true;
  }

  // Return false (no processing needed - fast path) only if all checks pass
  return false;
}

/**
 * Process audio in a single pass: downmix to mono, resample to target rate, and adjust amplitude
 * This combines multiple processing steps into one OfflineAudioContext render operation for better performance.
 */
async function processAudioInSinglePass(
  audioBuffer: AudioBuffer,
  options: {
    targetChannels: number;
    targetSampleRate: number;
    amplitudeLimit: number;
    amplitudeBoostThreshold: number;
    amplitudeBoostTarget: number;
  }
): Promise<AudioBuffer> {
  const {
    targetChannels,
    targetSampleRate,
    amplitudeLimit,
    amplitudeBoostThreshold,
    amplitudeBoostTarget,
  } = options;

  // Calculate the length of the output buffer based on target sample rate
  const outputLength = Math.ceil(audioBuffer.duration * targetSampleRate);

  // Create OfflineAudioContext with target channels, calculated length, and target sample rate
  const OfflineAudioContextCtor =
    window.OfflineAudioContext ||
    (window as typeof window & { webkitOfflineAudioContext?: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  if (!OfflineAudioContextCtor) {
    throw new Error("OfflineAudioContext not available for single-pass processing");
  }

  const offlineContext = new OfflineAudioContextCtor(
    targetChannels,
    outputLength,
    targetSampleRate
  );

  // Create buffer source node and set input audioBuffer as source
  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;

  // Calculate combined gain value based on amplitude limits
  const currentMax = getMaxAmplitude(audioBuffer);
  let gainMultiplier = 1.0;

  // Check if max amplitude exceeds limit (needs limiting) or is below boost threshold (needs boosting)
  if (currentMax > amplitudeLimit) {
    // Need to limit: reduce gain to bring max amplitude down to limit
    gainMultiplier = amplitudeLimit / currentMax;
  } else if (currentMax < amplitudeBoostThreshold) {
    // Need to boost: increase gain to bring max amplitude up to target
    gainMultiplier = amplitudeBoostTarget / currentMax;
  }
  // If amplitude is within acceptable range (between threshold and limit), gainMultiplier stays 1.0

  // Create gain node, set gain value, and connect audio graph: source → gain → destination
  const gainNode = offlineContext.createGain();
  gainNode.gain.value = gainMultiplier;

  source.connect(gainNode);
  gainNode.connect(offlineContext.destination);

  // Start source and call startRendering() once to get processed AudioBuffer
  source.start();
  const processedBuffer = await offlineContext.startRendering();

  // Return processed AudioBuffer
  return processedBuffer;
}

/**
 * Process audio with fallback to legacy pipeline if single-pass fails.
 * This provides failure-safe behavior: if single-pass processing fails, automatically
 * falls back to the legacy multi-pass pipeline to ensure encoding can still succeed.
 */
async function encodeWithFallback(
  audioBuffer: AudioBuffer,
  options: {
    targetChannels: number;
    targetSampleRate: number;
    amplitudeLimit: number;
    amplitudeBoostThreshold: number;
    amplitudeBoostTarget: number;
  }
): Promise<AudioBuffer> {
  try {
    // Try single-pass processing first (optimized path)
    return await processAudioInSinglePass(audioBuffer, options);
  } catch (error) {
    // Log error for debugging (fallback is silent to users)
    console.error("Single-pass processing failed, falling back to legacy pipeline:", error);
    
    try {
      // Fallback to legacy processing pipeline: downmix → resample → limit amplitude → boost amplitude
      let processedBuffer = await downmixToMono(audioBuffer);
      
      if (ENABLE_RESAMPLING) {
        processedBuffer = await resampleToCanonicalRate(processedBuffer);
      }
      
      processedBuffer = await limitAmplitude(processedBuffer, options.amplitudeLimit);
      processedBuffer = await boostLowAmplitude(
        processedBuffer,
        options.amplitudeBoostThreshold,
        options.amplitudeBoostTarget
      );
      
      // Return processed buffer from legacy path
      return processedBuffer;
    } catch (fallbackError) {
      // If both paths fail, re-throw error (existing error handling applies)
      throw new Error(
        `Both single-pass and legacy processing failed. Single-pass error: ${error instanceof Error ? error.message : String(error)}. Legacy error: ${fallbackError instanceof Error ? fallbackError.message : String(fallbackError)}`
      );
    }
  }
}

/**
 * Limit amplitude to prevent clipping (caps at 0.95)
 */
async function limitAmplitude(
  audioBuffer: AudioBuffer,
  maxAmplitude = 0.95
): Promise<AudioBuffer> {
  const currentMax = getMaxAmplitude(audioBuffer);
  
  // No limiting needed if already below threshold
  if (currentMax <= maxAmplitude) {
    return audioBuffer;
  }

  const gainMultiplier = maxAmplitude / currentMax;
  const OfflineAudioContextCtor =
    window.OfflineAudioContext ||
    (window as typeof window & { webkitOfflineAudioContext?: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  if (!OfflineAudioContextCtor) {
    throw new Error("OfflineAudioContext not available for limiting");
  }

  const offlineContext = new OfflineAudioContextCtor(
    1, // channels (mono)
    audioBuffer.length,
    audioBuffer.sampleRate
  );

  const source = offlineContext.createBufferSource();
  const gainNode = offlineContext.createGain();
  gainNode.gain.value = gainMultiplier;
  
  source.buffer = audioBuffer;
  source.connect(gainNode);
  gainNode.connect(offlineContext.destination);
  source.start();

  const limitedBuffer = await offlineContext.startRendering();
  return limitedBuffer;
}

/**
 * Boost low amplitude recordings to match target level
 */
async function boostLowAmplitude(
  audioBuffer: AudioBuffer,
  minAmplitude = 0.7,
  targetAmplitude = 0.85
): Promise<AudioBuffer> {
  const currentMax = getMaxAmplitude(audioBuffer);
  
  // No boosting needed if already above threshold
  if (currentMax >= minAmplitude) {
    return audioBuffer;
  }

  const gainMultiplier = targetAmplitude / currentMax;
  const OfflineAudioContextCtor =
    window.OfflineAudioContext ||
    (window as typeof window & { webkitOfflineAudioContext?: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  if (!OfflineAudioContextCtor) {
    throw new Error("OfflineAudioContext not available for boosting");
  }

  const offlineContext = new OfflineAudioContextCtor(
    1, // channels (mono)
    audioBuffer.length,
    audioBuffer.sampleRate
  );

  const source = offlineContext.createBufferSource();
  const gainNode = offlineContext.createGain();
  gainNode.gain.value = gainMultiplier;
  
  source.buffer = audioBuffer;
  source.connect(gainNode);
  gainNode.connect(offlineContext.destination);
  source.start();

  const boostedBuffer = await offlineContext.startRendering();
  return boostedBuffer;
}

type Mp3EncoderCtor = typeof import("@breezystack/lamejs").Mp3Encoder;

let cachedEncoderCtor: Mp3EncoderCtor | null = null;

async function getMp3EncoderCtor(): Promise<Mp3EncoderCtor> {
  if (cachedEncoderCtor) return cachedEncoderCtor;

  const lame = await import("@breezystack/lamejs");
  cachedEncoderCtor = lame.Mp3Encoder;
  return cachedEncoderCtor;
}

async function convertToMp3Blob(
  audioBuffer: AudioBuffer,
  bitrateKbps: number,
): Promise<Blob> {
  const Mp3Encoder = await getMp3EncoderCtor();

  const numChannels = audioBuffer.numberOfChannels > 1 ? 2 : 1;
  const left = audioBuffer.getChannelData(0);
  const right = numChannels === 2 ? audioBuffer.getChannelData(1) : left;

  const encoder = new Mp3Encoder(numChannels, audioBuffer.sampleRate, bitrateKbps);
  const mp3Chunks: Uint8Array[] = [];

  let sampleIndex = 0;

  while (sampleIndex < left.length) {
    const nextIndex = Math.min(sampleIndex + MAX_SAMPLES_PER_FRAME, left.length);

    const leftChunk = floatTo16BitPCM(left.subarray(sampleIndex, nextIndex));
    const rightChunk =
      numChannels === 2
        ? floatTo16BitPCM(right.subarray(sampleIndex, nextIndex))
        : undefined;

    const mp3buf =
      numChannels === 2 && rightChunk
        ? encoder.encodeBuffer(leftChunk, rightChunk)
        : encoder.encodeBuffer(leftChunk);

    if (mp3buf.length > 0) {
      mp3Chunks.push(mp3buf);
    }

    sampleIndex = nextIndex;
  }

  const endBuffer = encoder.flush();
  if (endBuffer.length > 0) {
    mp3Chunks.push(endBuffer);
  }

  return new Blob(mp3Chunks as BlobPart[], { type: "audio/mpeg" });
}

export function validateAudioConstraints(
  audio: EncodedAudio,
  options: AudioValidationOptions,
): AudioValidationResult {
  const { durationMs, sizeBytes } = audio;
  const { minDurationSeconds, maxDurationSeconds, maxSizeMB } = options;

  if (durationMs < minDurationSeconds * 1000) {
    return {
      ok: false,
      reason: `Posts must be at least ${minDurationSeconds} seconds.`,
    };
  }

  if (durationMs > maxDurationSeconds * 1000) {
    // Format duration in a user-friendly way (e.g., "2 minutes" instead of "120 seconds")
    const minutes = Math.floor(maxDurationSeconds / 60);
    const seconds = maxDurationSeconds % 60;
    const durationText =
      minutes > 0 && seconds === 0
        ? `${minutes} minute${minutes > 1 ? "s" : ""}`
        : `${maxDurationSeconds} seconds`;
    return {
      ok: false,
      reason: `Posts max out at ${durationText}.`,
    };
  }

  if (sizeBytes > maxSizeMB * 1024 * 1024) {
    return {
      ok: false,
      reason: `Keep the audio under ${maxSizeMB}MB (currently ${(sizeBytes / (1024 * 1024)).toFixed(2)}MB).`,
    };
  }

  return { ok: true };
}

export function pcmToWav(
  pcmData: Float32Array[],
  sampleRate: number,
  numChannels: number,
): Blob {
  const length = pcmData[0].length;
  const buffer = new ArrayBuffer(44 + length * numChannels * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + length * numChannels * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, length * numChannels * 2, true);

  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, pcmData[channel][i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: "audio/wav" });
}

export async function encodePcmToMp3(
  pcmData: Float32Array[],
  sampleRate: number,
  bitrateKbps = DEFAULT_BITRATE,
): Promise<EncodedAudio> {
  if (!pcmData || pcmData.length === 0 || !pcmData[0] || pcmData[0].length === 0) {
    throw new Error("PCM data is empty");
  }

  // Convert PCM data to AudioBuffer format for single-pass processing
  const AudioContextCtor =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error("AudioContext not available for audio processing");
  }

  // Phase 3.1: Enforce mono defensively - downmix if >1 channel
  let monoData: Float32Array;
  if (pcmData.length > 1) {
    const length = pcmData[0].length;
    monoData = new Float32Array(length);
    for (let i = 0; i < length; i++) {
      let sum = 0;
      for (let channel = 0; channel < pcmData.length; channel++) {
        sum += pcmData[channel][i];
      }
      monoData[i] = sum / pcmData.length;
    }
  } else {
    monoData = pcmData[0];
  }

  // Create AudioBuffer from PCM data for single-pass processing
  const context = new AudioContextCtor({ sampleRate });
  const inputBuffer = context.createBuffer(1, monoData.length, sampleRate);
  inputBuffer.copyToChannel(new Float32Array(monoData), 0);
  await context.close();

  // Fast-path detection: skip processing if audio is already optimal
  let finalData: Float32Array;
  let finalSampleRate: number;
  if (needsAudioProcessing(inputBuffer)) {
    // Process audio with fallback to legacy pipeline if single-pass fails
    const processedBuffer = await encodeWithFallback(inputBuffer, {
      targetChannels: 1,
      targetSampleRate: CANONICAL_SAMPLE_RATE,
      amplitudeLimit: 0.95,
      amplitudeBoostThreshold: 0.7,
      amplitudeBoostTarget: 0.85,
    });

    // Extract processed audio data from returned AudioBuffer for MP3 encoding
    finalData = processedBuffer.getChannelData(0);
    finalSampleRate = processedBuffer.sampleRate;
  } else {
    // Fast-path: skip processing and proceed directly to MP3 encoding
    finalData = inputBuffer.getChannelData(0);
    finalSampleRate = inputBuffer.sampleRate;
  }

  const Mp3Encoder = await getMp3EncoderCtor();
  const encoder = new Mp3Encoder(1, finalSampleRate, bitrateKbps); // Always mono now
  const mp3Chunks: Uint8Array[] = [];

  let sampleIndex = 0;

  while (sampleIndex < finalData.length) {
    const nextIndex = Math.min(sampleIndex + MAX_SAMPLES_PER_FRAME, finalData.length);

    const chunk = floatTo16BitPCM(finalData.subarray(sampleIndex, nextIndex));
    const mp3buf = encoder.encodeBuffer(chunk);

    if (mp3buf.length > 0) {
      mp3Chunks.push(mp3buf);
    }

    sampleIndex = nextIndex;
  }

  const endBuffer = encoder.flush();
  if (endBuffer.length > 0) {
    mp3Chunks.push(endBuffer);
  }

  const mp3Blob = new Blob(mp3Chunks as BlobPart[], { type: "audio/mpeg" });
  
  // Calculate duration from audio buffer properties (replaces mandatory decode verification)
  // Accuracy: ±20ms tolerance is acceptable for user-facing duration display and validation
  const actualDurationMs = Math.round((finalData.length / finalSampleRate) * 1000);

  return {
    mp3Blob,
    durationMs: actualDurationMs,
    sizeBytes: mp3Blob.size,
    sampleRate: finalSampleRate, // Will be CANONICAL_SAMPLE_RATE
  };
}

